import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { useNavigate, Link as RouterLink } from 'react-router'
import { useLazyQuery, useMutation } from '@apollo/client'
import { FormattedMessage } from 'react-intl'

import { OrganisationSites as GetSites, UpdateUserSettings } from '../../queries/NavigationQueries.gql'

import { Menu, MenuTrigger } from './Menu'

import SearchHOC from './SearchHOC'
import SearchInput from './SearchInput'
import { SearchIcon } from '../Icon'
import { Flex, Box } from '../Grid'
import { Heading, Text } from '../Type'

import { useSession } from '../../providers/SessionProvider'

const Overlay = styled.div`
  bottom: 0;
  left: 0;
  visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
  position: fixed;
  right: 0;
  top: 0;
  z-index: 11;
  background: rgba(0, 0, 0, 0.7);
`

const Wrapper = styled.div`
  width: 100%;
`

const ResultsWrapper = styled(Box)`
  left: 50%;
  top: 25%;
  max-width: 600px;
  border: 1px solid var(--floating-element-border);
  opacity: ${({ active }) => (active ? 1 : 0)};
  position: absolute;
  transform: translate(-50%, 25%);
  transition: opacity 150ms linear;
  visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
  width: 100%;
  z-index: 11;
`

const ResultsItems = styled.div`
  max-height: 360px;
  overflow: auto;
  padding-bottom: 5px;
`

const Results = ({ children, ...props }) => {
  return children ? (
    <ResultsWrapper
      borderRadius="3px"
      backgroundColor="var(--floating-element-background)"
      boxShadow="menu"
      pt={2}
      {...props}
    >
      {children}
      <Box borderTopWidth={1} borderColor="var(--floating-element-border)" borderTopStyle="solid" p={3}>
        <Flex alignItems="center">
          <Box mr={3}>
            <FormattedMessage id="navigation.search.instructions.select" />
          </Box>
          <Box>
            <FormattedMessage id="navigation.search.instructions.open" />
          </Box>
        </Flex>
      </Box>
    </ResultsWrapper>
  ) : null
}

const ItemWrapper = styled(Box)`
  border-radius: 3px;
  background: ${({ active }) =>
    active ? 'light-dark(var(--brand-blue-100), var(--brand-blue-400))' : 'light-dark(white, var(--brand-grey-600))'};
  color: ${({ active }) =>
    active ? 'light-dark(var(--brand-blue-400), var(--brand-blue-50))' : 'light-dark(inherit, var(--brand-grey-100))'};
  position: relative;

  &:focus,
  &:hover {
    background: ${({ disabled }) => !disabled && 'light-dark(var(--brand-blue-100), var(--brand-blue-400))'};
    > * {
      color: ${({ disabled }) => !disabled && 'light-dark(var(--brand-blue-400), var(--brand-blue-50))'};
    }
  }
`
ItemWrapper.propTypes = {
  active: PropTypes.bool
}

const Title = ({ children, ...props }) => (
  <Box px={3} py={2} {...props}>
    <Heading level="xs" color="light-dark(var(--brand-grey-500), var(--brand-grey-50))">
      {children}
    </Heading>
  </Box>
)

Title.propTypes = {
  children: PropTypes.any
}

const Item = ({ children, ...props }) => {
  return (
    <Box px={2}>
      <ItemWrapper backgroundColor="white" p={2} width={1} {...props}>
        {children}
      </ItemWrapper>
    </Box>
  )
}

const LinkIndicator = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  background-color: var(--brand-blue-50);
  border: 1px solid var(--brand-blue-200);
  border-radius: 3px;
  padding: 0 6px;
`

const Link = React.forwardRef(function link(link, ref) {
  const { name, teamName, active, to, onClick, ...props } = link
  return (
    <RouterLink to={to} onClick={() => onClick(link)} ref={ref}>
      <Item active={active} {...props}>
        <Text as="div" color={active ? 'inherit' : 'light-dark(var(--brand-grey-400), var(--brand-grey-100))'}>
          {name}
        </Text>
        <Text as="div" level="xs" color={active ? 'inherit' : 'grey300'}>
          in {teamName}
        </Text>
        {active ? (
          <LinkIndicator>
            <Text level="xs" color="blue400">
              ⤶
            </Text>
          </LinkIndicator>
        ) : null}
      </Item>
    </RouterLink>
  )
})

Link.propTypes = {
  active: PropTypes.bool,
  name: PropTypes.string.isRequired,
  teamName: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired
}

const generateLinks = sites => {
  // HACK alert: if we detect 'crux' at the end of the current path, assume we're viewing a crux report
  return sites.map(({ name, slug, team: { name: teamName, slug: teamId } }) => ({
    name,
    teamName,
    slug,
    to: document.location.pathname.match(/crux|crux\/pages$/)
      ? `/teams/${teamId}/${slug}/crux`
      : `/teams/${teamId}/${slug}`,
    ref: React.createRef()
  }))
}
const NavSearch = ({ orgId, teamId }) => {
  const { currentOrganisationId, currentUser } = useSession({
    orgId,
    teamId
  })
  const navigate = useNavigate()
  const searchRef = useRef()
  const [searching, setSearching] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [resultsType, setResultsType] = useState('saved')
  const [focusIndex, setFocusIndex] = useState(0)
  const [keyboardControl, setKeyboardControl] = useState(true)
  const [links, setLinks] = useState([])
  const [saved, setSaved] = useState(generateLinks(currentUser?.recentSites || []))
  const [getData] = useLazyQuery(GetSites, {
    variables: {
      orgId: currentOrganisationId,
      sitesFilter: { nameContains: searchTerm }
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: data => {
      const { organisation } = data || {}
      const {
        sitesList: { edges }
      } = organisation || { sitesList: {} }
      if (!searchTerm.length) return
      setLinks(generateLinks((edges || []).map(({ node }) => node)))
      setResultsType('results')
    }
  })
  const [updateRecentSites] = useMutation(UpdateUserSettings, {
    onCompleted: ({ updateUserSettings: { recentSites } }) => {
      setSaved(generateLinks(recentSites || []))
    }
  })

  useEffect(() => {
    document.body.style.overflow = searching ? 'hidden' : 'auto'

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [searching])

  useEffect(() => {
    if (searchTerm.length) {
      getData()
      setSearching(true)
    } else {
      setResultsType('saved')
      setLinks(saved)
    }
  }, [searchTerm])

  const focusOnIndex = index => {
    setFocusIndex(index)
    const link = links[index]
    if (link) {
      link.ref?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start'
      })
    }
  }

  const clearSearch = () => {
    setSearching(false)
    searchRef.current.value = ''
    setFocusIndex(0)
    setKeyboardControl(true)
    searchRef.current.blur()
  }

  const resetSearch = () => {
    setSearchTerm('')
    setLinks(saved)
    setResultsType('saved')
    clearSearch()
  }

  const saveLink = link => {
    let updatedSaved = saved
    const index = saved.findIndex(({ slug }) => link.slug === slug)
    if (index >= 0) {
      updatedSaved.splice(index, 1)
    }
    updatedSaved.splice(0, 0, link)
    if (updatedSaved.length > 5) updatedSaved.splice(4, updatedSaved.length - 5)
    updateRecentSites({
      variables: { recentSites: saved.map(({ slug }) => slug) }
    })
  }

  const goToSite = () => {
    const link = links[focusIndex]
    if (link) {
      navigate(link.to)
      saveLink(link)
    }
    resetSearch()
  }

  const handleInputKeyUp = event => {
    switch (event.keyCode) {
      case 38:
        // up
        if (focusIndex > 0) {
          focusOnIndex(focusIndex - 1)
        } else {
          focusOnIndex(links.length - 1)
        }
        setKeyboardControl(true)
        return
      case 40:
        // down
        if (focusIndex < links.length - 1) {
          focusOnIndex(focusIndex + 1)
        } else {
          focusOnIndex(0)
        }
        setKeyboardControl(true)
        return
      case 13:
        // enter
        goToSite()
        return
      default:
        return
    }
  }

  const handleClickLink = link => {
    saveLink(link)
    resetSearch()
  }

  const startSearch = () => {
    setSearching(true)
    searchRef.current.focus()
  }

  const { formatMessage } = useIntl()

  const isWindows = window?.navigator?.platform?.indexOf('Win') > -1

  return (
    <SearchHOC onSearch={startSearch} onCancel={clearSearch}>
      <Overlay active={searching} onClick={() => searching && setSearching(false)} />
      <Wrapper>
        <Box width={1} maxWidth="280px" mx="auto">
          <Menu>
            <MenuTrigger
              aria-label={formatMessage({ id: 'navigation.links.search.triggerLabel' })}
              aria-haspopup="dialog"
              aria-controls="search-modal"
              title={formatMessage({ id: `navigation.links.search.${isWindows ? 'windows' : 'mac'}` })}
            >
              <SearchIcon onClick={startSearch} />
            </MenuTrigger>
          </Menu>
        </Box>
        <Results id="search-modal" active={searching} data-testid="navigationSearchResults">
          <Box px={2} mb={1}>
            <FormattedMessage id="navigation.menu.sites.search">
              {label => (
                <SearchInput
                  data-testid="navigationSearch"
                  active={searching}
                  ref={searchRef}
                  onFocus={startSearch}
                  onChange={setSearchTerm}
                  placeholder={label}
                  width={1}
                  onKeyUp={handleInputKeyUp}
                />
              )}
            </FormattedMessage>
          </Box>
          <ResultsItems>
            {links.length ? (
              <>
                <Title>
                  <FormattedMessage id={`navigation.search.${resultsType}.title`} />
                </Title>
                {links.map((link, index) => (
                  <Link
                    key={index}
                    {...link}
                    disabled={keyboardControl}
                    active={keyboardControl && focusIndex === index}
                    onClick={handleClickLink}
                    onMouseEnter={() => {
                      setFocusIndex(index)
                      setKeyboardControl(false)
                    }}
                  />
                ))}
              </>
            ) : (
              <>
                <Title>
                  <FormattedMessage id="navigation.search.no_results.title" />
                </Title>
                <Item disabled={true} p={null} pt={0} px={2} pb={2}>
                  <Text>
                    <FormattedMessage id="navigation.search.no_results.description" />
                  </Text>
                </Item>
              </>
            )}
          </ResultsItems>
        </Results>
      </Wrapper>
    </SearchHOC>
  )
}

export default NavSearch
